/**
 * QorTrace · Forgot / Reset password (Phase 5.6 · iter 4.4)
 *
 * Two single-file public pages:
 *   /account/forgot-password   — submit email → backend mints + emails token
 *   /account/reset-password?token=… — set a new password using the token
 *
 * Always returns a friendly success state on the forgot step (no user
 * enumeration). The reset step surfaces real errors (expired, weak pw).
 */
import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2, KeyRound, MailCheck, CheckCircle2 } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

const API = process.env.REACT_APP_BACKEND_URL;
const AX  = axios.create({ baseURL: API });


function Shell({ eyebrow, title, subtitle, children, testid }) {
  return (
    <div data-testid={testid} className="min-h-[100vh]"
         style={{ background: "var(--qt-bg, #040914)", color: "var(--qt-text-1, #E8F0FB)" }}>
      <div className="max-w-[460px] mx-auto px-6 py-16">
        <div className="qt-label" style={{ color: "var(--qt-cyan)", margin: 0 }}>{eyebrow}</div>
        <h1 className="mt-2" style={{
          fontSize: 30, lineHeight: 1.1, fontWeight: 700, color: "#fff",
        }}>{title}</h1>
        {subtitle && (
          <p className="mt-3" style={{
            color: "var(--qt-text-2)", fontSize: 14, lineHeight: 1.6,
          }}>{subtitle}</p>
        )}
        <div className="mt-7">{children}</div>
        <div className="mt-8 text-xs">
          <Link to="/signin"
                className="qt-mono"
                data-testid="forgot-back-signin"
                style={{ color: "var(--qt-text-3)", letterSpacing: "0.05em",
                         textDecoration: "none" }}>
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}


export function ForgotPassword() {
  useDocumentTitle("Reset your password — QorTrace");
  const [email, setEmail] = useState("");
  const [sent, setSent]   = useState(false);
  const [busy, setBusy]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await AX.post("/api/account/forgot-password",
                    { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (_e) {
      // Endpoint always returns 200 to prevent enumeration — fall through.
      setSent(true);
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <Shell eyebrow="PASSWORD RESET"
             title="Check your inbox"
             testid="forgot-password-sent"
             subtitle={`If an account exists for ${email}, we just emailed a reset link. The link expires in 1 hour.`}>
        <div className="px-4 py-4 rounded inline-flex items-center gap-2"
             style={{ background: "rgba(31,221,131,0.08)",
                      border: "1px solid rgba(31,221,131,0.30)",
                      color: "#1FDD83", fontSize: 13 }}>
          <MailCheck size={15} /> Email dispatched (best-effort)
        </div>
        <p className="mt-5 text-[12.5px]" style={{ color: "var(--qt-text-3)" }}>
          Didn't get it? Check spam, then try again in a few minutes.
          Marketing-list opt-outs do NOT block transactional resets — but a
          hard-bounce on your address will. If you're stuck, contact{" "}
          <a href="mailto:support@qortrace.com" style={{ color: "var(--qt-cyan)" }}>
            support@qortrace.com
          </a>.
        </p>
      </Shell>
    );
  }

  return (
    <Shell eyebrow="PASSWORD RESET"
           title="Forgot your password?"
           testid="forgot-password-form"
           subtitle="Enter the email tied to your QorTrace account and we'll send you a one-time reset link.">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3">
        <label className="qt-label" style={{ margin: 0, color: "var(--qt-text-3)" }}>EMAIL</label>
        <input type="email" required value={email}
               onChange={(e) => setEmail(e.target.value)}
               data-testid="forgot-email-input"
               autoFocus
               placeholder="you@company.com"
               className="qt-input" />
        <button type="submit" disabled={busy || !email.trim()}
                data-testid="forgot-submit"
                className="qt-btn qt-btn-primary mt-2 inline-flex items-center justify-center gap-2"
                style={{ fontSize: 13 }}>
          {busy
            ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
            : <>Send reset link <ArrowRight size={14} /></>}
        </button>
      </form>
    </Shell>
  );
}


export function ResetPassword() {
  useDocumentTitle("Set a new password — QorTrace");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) {
      setErr("This reset link is missing its token. Request a new one.");
      return;
    }
    if (pw1 !== pw2) {
      setErr("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await AX.post("/api/account/reset-password",
                    { token, password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/signin?mode=login"), 2200);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Reset failed. Try requesting a new link.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Shell eyebrow="PASSWORD RESET"
             title="Password updated"
             testid="reset-password-done"
             subtitle="Redirecting you to sign in…">
        <div className="px-4 py-4 rounded inline-flex items-center gap-2"
             style={{ background: "rgba(31,221,131,0.08)",
                      border: "1px solid rgba(31,221,131,0.30)",
                      color: "#1FDD83", fontSize: 13 }}>
          <CheckCircle2 size={15} /> Your new password is live.
        </div>
      </Shell>
    );
  }

  return (
    <Shell eyebrow="PASSWORD RESET"
           title="Set a new password"
           testid="reset-password-form"
           subtitle="Pick a strong password (9+ chars, upper + lower + number + special). You'll be signed out of any existing sessions.">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3">
        <label className="qt-label" style={{ margin: 0, color: "var(--qt-text-3)" }}>NEW PASSWORD</label>
        <input type="password" required value={pw1}
               onChange={(e) => setPw1(e.target.value)}
               data-testid="reset-password-input"
               autoFocus
               placeholder="9+ chars · upper/lower/number/special"
               className="qt-input"
               minLength={9} />

        <label className="qt-label" style={{ margin: 0, color: "var(--qt-text-3)" }}>CONFIRM</label>
        <input type="password" required value={pw2}
               onChange={(e) => setPw2(e.target.value)}
               data-testid="reset-password-confirm"
               placeholder="Re-type the new password"
               className="qt-input"
               minLength={9} />

        {err && (
          <div className="qt-mono text-xs px-3 py-2 rounded"
               data-testid="reset-password-error"
               style={{ background: "rgba(255,110,110,0.08)",
                        border: "1px solid rgba(255,110,110,0.30)",
                        color: "#FF6E6E" }}>
            {err}
          </div>
        )}

        <button type="submit" disabled={busy || !pw1 || !pw2}
                data-testid="reset-password-submit"
                className="qt-btn qt-btn-primary mt-2 inline-flex items-center justify-center gap-2"
                style={{ fontSize: 13 }}>
          {busy
            ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
            : <><KeyRound size={13} /> Set new password</>}
        </button>
      </form>
    </Shell>
  );
}
